import React, { useState } from 'react';
import { ProfilePicture } from '@getro/rombo';
import { Flex, Box, Text } from 'rebass/styled-components';
import PropTypes from 'prop-types';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { RefreshContactModal } from '../../../../../../components/molecules/refreshContactModal';
import { useIsSafari } from '../../../../../../hooks/useIsSafari';

export const ContactKanbanCard = ({ card, state, innerRef, innerStyles }) => {
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const isSafari = useIsSafari();
  const history = useHistory();
  const { url } = useRouteMatch();

  return (
    <Flex
      minHeight="62px"
      sx={{
        borderRadius: '8px',
        bg: 'neutral.0',
        p: '8px',
        gap: '8px',
        border: '1px solid',
        borderColor: 'border.subtle',
        boxShadow: '0 1px 0 0 rgba(0, 0, 0, 0.06)',
        transition: 'border-color 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
        ...innerStyles[state.type],
        '&:hover': {
          borderColor: 'purple.300',
          boxShadow: '0 1px 2px 0 rgba(27, 36, 50, 0.04), 0 1px 16px 0 rgba(27, 36, 50, 0.06)',
        },
      }}
      ref={innerRef}
      style={
        state.type === 'preview'
          ? {
              width: state.dragging.width,
              height: state.dragging.height,
              transform: !isSafari ? 'rotate(4deg)' : undefined,
            }
          : undefined
      }
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        history.push(`${url}/${card.contact.id}${window.location.search}`);
      }}
    >
      <Box width="40px" height="40px" flex="0 0 auto">
        <ProfilePicture
          name={card.contact.fullName || card.contact.linkedinHandle}
          imageUrl={card.contact.profilePicUrl}
        />
      </Box>
      <Flex flexDirection="column" flexGrow="1" sx={{ gap: '4px' }}>
        <Flex alignItems="center" gap="4px">
          <Text fontSize="14px" fontWeight="500" color="text.dark" flexGrow="1">
            {card.contact.fullName || card.contact.linkedinHandle}
          </Text>
        </Flex>
        <>
          {card.contact.enrichmentStatus !== 'enriched' ? (
            <Text sx={{ fontStyle: 'italic', fontSize: '14px', color: 'text.subtle' }}>
              {`We couldn't enrich this contact automatically.`}{' '}
              <Text
                as="span"
                sx={{ cursor: 'pointer', textDecoration: 'underline', '&:hover': { color: 'purple.500' } }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRefreshModal(true);
                }}
              >
                Learn more.
              </Text>
            </Text>
          ) : (
            <>
              {card?.contact?.primaryActivePosition ? (
                <Text
                  fontSize="14px"
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    '-webkit-line-clamp': '2',
                    '-webkit-box-orient': 'vertical',
                    whiteSpace: 'normal',
                  }}
                >
                  {card.contact.primaryActivePosition.jobTitle}
                  <Text as="span" color="text.subtle">{` at `}</Text>
                  {card.contact.primaryActivePosition.companyName}
                </Text>
              ) : (
                <Text
                  fontSize="14px"
                  color="text.subtle"
                  sx={{ fontStyle: 'italic', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                >
                  No current position
                </Text>
              )}
              {card?.contact.location?.description && (
                <Text fontSize="14px" sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                  {card.contact.location.description}
                </Text>
              )}
            </>
          )}
        </>
      </Flex>
      {showRefreshModal && (
        <RefreshContactModal onCloseModal={() => setShowRefreshModal(false)} linkedinUrl={card.contact.linkedinUrl} />
      )}
    </Flex>
  );
};

ContactKanbanCard.propTypes = {
  card: PropTypes.object.isRequired,
  state: PropTypes.object.isRequired,
  innerRef: PropTypes.object,
  innerStyles: PropTypes.object.isRequired,
};

ContactKanbanCard.defaultProps = {
  innerRef: null,
};
